import { Lap } from '../lap/Lap'
import { buildLap } from '../lap/buildLap'
import { RawTelemetryData } from '../telemetry/raw/RawTelemetryData'
import { ProcessedTelemetryData } from '../telemetry/processed/ProcessedTelemetryData'
import { GpsPoint } from '../telemetry/processed/GpsPoint'
import { TrackSessionMetadata, WeatherCondition } from './TrackSessionMetadata'
import { TrackSessionStatus } from './TrackSessionStatus'
import { findFinishLineCrossings } from './finishLineCrossings'
import { calculateDistance } from '../../utils/distanceUtils'
import { generateUuid, isEmptyGuid } from '../../utils/guidUtils'
import {
  Coordinate,
  Device,
  Guid,
  TrackSessionMetadataDto,
} from '../../api/apiTypes'
import { LatLng } from '../../types/LatLng'

/**
 * Features available in a session (e.g., video, lean angle data)
 */
export interface SessionFeatures {
  hasVideo: boolean
  hasLeanAngle: boolean
}

/**
 * Track information compatible with both TrackDto and domain Track
 * This allows TrackSession to work with either format
 */
export interface TrackInfo {
  id: string
  name: string
  finishLine?: Coordinate[]
  countryCode?: string
  isUserSuggested?: boolean
}

export class TrackSession {
  // Mutable to allow updating after saving to backend
  id: string
  readonly name: string
  // Mutable to allow updating track after user suggestion
  track: TrackInfo
  readonly driverName: string
  readonly vehicleId?: string
  readonly vehicleName?: string
  readonly recordingDate: Date
  readonly weather?: WeatherCondition
  readonly notes?: string
  readonly sourceFileName?: string
  readonly device: Device

  private _laps: Map<number, Lap>
  private _status: TrackSessionStatus
  private _rawTelemetry: RawTelemetryData
  private _processedTelemetry: ProcessedTelemetryData
  private _isStoredInBackend: boolean
  private _sessionFeatures: SessionFeatures
  private _lapsIdentifiers?: Record<number, Guid>
  private _metadata?: TrackSessionMetadataDto
  private _sharedLapIds?: Set<string>

  constructor(
    metadata: TrackSessionMetadata,
    track: TrackInfo,
    processedTelemetry: ProcessedTelemetryData,
    rawTelemetry: RawTelemetryData,
    device: Device,
    sessionFeatures: SessionFeatures = { hasVideo: false, hasLeanAngle: false },
    status: TrackSessionStatus = TrackSessionStatus.Processing
  ) {
    this.id = metadata.sessionId
    this.name = metadata.sessionName
    this.track = track
    this.driverName = metadata.driverName
    this.vehicleId = metadata.vehicleId
    this.vehicleName = metadata.vehicleName
    this.recordingDate = metadata.recordingDate
    this.sourceFileName = metadata.sourceFileName
    this.weather = metadata.weather
    this.notes = metadata.notes
    this.device = device
    this._laps = new Map()
    this._processedTelemetry = processedTelemetry
    this._rawTelemetry = rawTelemetry
    this._status = status
    this._isStoredInBackend = false
    this._sessionFeatures = sessionFeatures
  }

  get status(): TrackSessionStatus {
    return this._status
  }

  get laps(): Lap[] {
    return Array.from(this._laps.values()).sort(
      (a, b) => a.lapNumber - b.lapNumber
    )
  }

  /**
   * Replace the lap array entirely
   * @deprecated Prefer using addLap/removeLap methods. This setter exists for backward compatibility.
   */
  set laps(value: Lap[]) {
    this._laps.clear()
    value.forEach((lap) => this._laps.set(lap.lapNumber, lap))
  }

  get lapCount(): number {
    return this._laps.size
  }

  get isStoredInBackend(): boolean {
    return this._isStoredInBackend
  }

  /** @deprecated Use isStoredInBackend instead */
  get isStored(): boolean {
    return this._isStoredInBackend
  }

  /** @deprecated Use markAsStoredInBackend() instead */
  set isStored(value: boolean) {
    this._isStoredInBackend = value
    if (value && this._status === TrackSessionStatus.Processing) {
      this._status = TrackSessionStatus.Completed
    }
  }

  get rawTelemetry(): RawTelemetryData {
    return this._rawTelemetry
  }

  get processedTelemetry(): ProcessedTelemetryData {
    return this._processedTelemetry
  }

  /** Alias for processedTelemetry to match web app naming */
  get telemetry(): ProcessedTelemetryData {
    return this._processedTelemetry
  }

  get sessionFeatures(): SessionFeatures {
    return this._sessionFeatures
  }

  get lapsIdentifiers(): Record<number, Guid> | undefined {
    return this._lapsIdentifiers
  }

  get metaData(): TrackSessionMetadataDto | undefined {
    return this._metadata
  }

  get sharedLapIds(): Set<string> | undefined {
    return this._sharedLapIds
  }

  /** Alias for name to support legacy web app code */
  get fileName(): string {
    return this.name
  }

  set lapsIdentifiers(value: Record<number, Guid> | undefined) {
    this._lapsIdentifiers = value
  }

  set metaData(value: TrackSessionMetadataDto | undefined) {
    this._metadata = value
  }

  set sharedLapIds(value: Set<string> | undefined) {
    this._sharedLapIds = value
  }

  addLap(lap: Lap): void {
    if (lap.sessionId !== this.id) {
      throw new Error(`Lap ${lap.id} does not belong to this track session`)
    }
    this._laps.set(lap.lapNumber, lap)
  }

  getLap(lapNumber: number): Lap | undefined {
    return this._laps.get(lapNumber)
  }

  getLapById(lapId: string): Lap | undefined {
    return Array.from(this._laps.values()).find((lap) => lap.id === lapId)
  }

  removeLap(lapNumber: number): void {
    this._laps.delete(lapNumber)
  }

  setLaps(lapIds: Record<number, Guid>): void {
    const currentLaps = this.laps
    this._laps.clear()

    currentLaps.forEach((lap, index) => {
      const newLapId = lapIds[index]
      this._laps.set(lap.lapNumber, newLapId ? lap.withId(newLapId) : lap)
    })
  }

  setStatus(status: TrackSessionStatus): void {
    this._status = status
  }

  setRawTelemetry(telemetry: RawTelemetryData): void {
    this._rawTelemetry = telemetry
  }

  setProcessedTelemetry(telemetry: ProcessedTelemetryData): void {
    this._processedTelemetry = telemetry
  }

  /** Alias for setProcessedTelemetry to match web app naming */
  setTelemetry(telemetry: ProcessedTelemetryData): void {
    this._processedTelemetry = telemetry
  }

  markAsStoredInBackend(): void {
    this._isStoredInBackend = true
    if (this._status === TrackSessionStatus.Processing) {
      this._status = TrackSessionStatus.Completed
    }
  }

  /**
   * Check if track is unknown based on name
   */
  private isTrackUnknown(): boolean {
    return this.track.name === 'Unknown'
  }

  hasKnownTrack(): boolean {
    return !this.isTrackUnknown() && !isEmptyGuid(this.track.id)
  }

  getBestLap(): Lap | undefined {
    if (this.lapCount === 0) return undefined
    return this.laps.reduce((best, current) =>
      current.lapTime < best.lapTime ? current : best
    )
  }

  getAverageLapTime(): number | undefined {
    if (this.lapCount === 0) return undefined
    const totalTime = this.laps.reduce((sum, lap) => sum + lap.lapTime, 0)
    return totalTime / this.lapCount
  }

  getTotalDistance(): number {
    if (this.lapCount === 0) return 0
    return this.laps.reduce((sum, lap) => sum + lap.getLapDistance(), 0)
  }

  getTotalDuration(): number {
    if (this.lapCount === 0) return 0
    const firstLap = this.laps[0]
    const lastLap = this.laps[this.lapCount - 1]
    return (
      lastLap.bounds.finishTime.getTime() - firstLap.bounds.startTime.getTime()
    )
  }

  hasLaps(): boolean {
    return this.lapCount > 0
  }

  isValid(): boolean {
    return this.track.id !== undefined && this.driverName !== undefined
  }

  /**
   * Apply start/finish line to detect laps in the session telemetry
   * @param sessionName Session name/number for lap grouping
   * @param finishLine Two points defining the start/finish line
   */
  applyStartFinishForSession(sessionName: number, finishLine: LatLng[]): void {
    let internalFinishLine = finishLine

    // Use track's finish line if available (supports both 1-point and 2-point)
    if (
      !this.isTrackUnknown() &&
      this.track.finishLine &&
      this.track.finishLine.length >= 1
    ) {
      internalFinishLine = this.track.finishLine.map((c) => ({
        latitude: c.latitude,
        longitude: c.longitude,
      }))
    }

    const gpsPoints = this._processedTelemetry.gpsPoints
    if (
      !gpsPoints ||
      gpsPoints.length === 0 ||
      internalFinishLine.length === 0
    ) {
      throw new Error('Invalid telemetry or finish line')
    }

    const crossingTimes: Date[] =
      internalFinishLine.length >= 2
        ? findFinishLineCrossings(gpsPoints, internalFinishLine).map(
            (crossing) => crossing.time
          )
        : this.findNearestApproachCrossings(
            gpsPoints,
            internalFinishLine[0]
          ).map((index) => gpsPoints[index].timestamp)

    if (crossingTimes.length === 0) {
      return // No laps detected
    }

    // Store existing selections and shared state
    const sharedLaps = this.laps.filter((lap) => lap.isShared)
    const existingSelections = new Map(
      this.laps.map((lap) => [lap.lapNumber, lap.isSelected])
    )

    // Clear existing laps
    this._laps.clear()

    for (let i = 0; i < crossingTimes.length - 1; i++) {
      const lapStartTime = crossingTimes[i]
      const lapFinishTime = crossingTimes[i + 1]

      let lapId = generateUuid()
      if (this._lapsIdentifiers?.[i]) {
        lapId = this._lapsIdentifiers[i]
      }

      const lap = buildLap({
        metadata: {
          lapId,
          lapNumber: i,
          driverName: this.driverName || 'Unknown',
          trackSessionId: this.id,
          trackId: this.track.id,
        },
        window: { startTime: lapStartTime, finishTime: lapFinishTime },
        sessionTelemetry: this._processedTelemetry,
        sessionRawTelemetry: this._rawTelemetry,
        device: this.device,
        sessionName: sessionName.toString(),
      })
      if (!lap) continue

      this._laps.set(i, lap)
    }

    if (this._laps.size === 0) {
      return // No complete laps found
    }

    // Restore shared and selection state
    for (const lap of this.laps) {
      const isInSharedSet = this._sharedLapIds?.has(lap.id)
      const wasSharedBefore = sharedLaps.some(
        (l) => l.lapNumber === lap.lapNumber
      )

      if (wasSharedBefore || isInSharedSet) {
        lap.isShared = true
      }

      if (existingSelections.has(lap.lapNumber)) {
        lap.isSelected = existingSelections.get(lap.lapNumber) ?? false
      }
    }
  }

  /**
   * Find lap crossings using nearest-approach to a single reference point.
   * Scans GPS points for "windows" where distance to the reference drops
   * below a threshold, and picks the closest point in each window.
   */
  private findNearestApproachCrossings(
    gpsPoints: GpsPoint[],
    refPoint: LatLng,
    thresholdMeters: number = 50,
    minLapTimeMs: number = 10000
  ): number[] {
    const crossings: number[] = []
    let inWindow = false
    let windowMinDist = Infinity
    let windowMinIndex = -1
    let lastCrossingTime = -Infinity

    for (let i = 0; i < gpsPoints.length; i++) {
      const dist = calculateDistance(
        gpsPoints[i].latitude,
        gpsPoints[i].longitude,
        refPoint.latitude,
        refPoint.longitude
      )

      if (dist < thresholdMeters) {
        if (!inWindow) {
          inWindow = true
          windowMinDist = dist
          windowMinIndex = i
        } else if (dist < windowMinDist) {
          windowMinDist = dist
          windowMinIndex = i
        }
      } else if (inWindow) {
        inWindow = false
        const crossingTime = gpsPoints[windowMinIndex].timestamp.getTime()
        if (crossingTime - lastCrossingTime >= minLapTimeMs) {
          crossings.push(windowMinIndex)
          lastCrossingTime = crossingTime
        }
        windowMinDist = Infinity
        windowMinIndex = -1
      }
    }

    // Handle track ending inside a window
    if (inWindow && windowMinIndex >= 0) {
      const crossingTime = gpsPoints[windowMinIndex].timestamp.getTime()
      if (crossingTime - lastCrossingTime >= minLapTimeMs) {
        crossings.push(windowMinIndex)
      }
    }

    return crossings
  }
}
